"use client";

/**
 * The early-access form, opened by every "Get your API key" / "Get early
 * access" button (Ticket 030, 2026-10-02).
 *
 * It posts to the control plane through the relay,
 * `POST /api/cp/onboarding/early-access`, never to the UI's old
 * `/api/early-access` route: on SCO the load balancer sends exactly that path
 * to the UI, so it would never reach the backend. The backend stores the
 * request, emails "we received your request", and notifies the team; an
 * operator's approval then creates the organisation and emails an activation
 * link.
 *
 * What stops abuse, outside to in: the relay limits how often one address can
 * submit; a hidden `website` field that people never fill (the backend answers
 * 202 to a filled one and stores nothing); a form sent faster than a person
 * can type is shown success and not sent; and the backend sends one address at
 * most five acknowledgements a day. Every outcome a stranger can observe is
 * the same "check your inbox", so the form reveals nothing about who has
 * already asked.
 *
 * When the backend's sign-up mode is "open" (launch), `open()` sends people
 * to `/dashboard/signup` instead, so the buttons flip with the backend and no
 * release is needed.
 *
 * The form always tries the endpoint first, whatever the mode check says:
 * since 2026-10-08 the relay can send this one call to a different backend
 * (`EARLY_ACCESS_BASE_URL`, the sovereign one on `main`), so a GCP mode check
 * answering 404 no longer means the endpoint is missing. Only if the endpoint
 * itself answers 404 does the form fall back to the UI's own
 * `/api/early-access` route (`legacyEarlyAccess`).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LogoMark } from "@/components/ui/logo";
import { requestEarlyAccess } from "@/lib/api/client";
import { loadSignupMode } from "@/lib/api/signup-mode";
import { FormConsent } from "@/components/legal/form-consent";
import { clearFormToken, formToken } from "@/lib/form-token";

/**
 * Today's behaviour on a backend without Ticket 030: the UI's own route,
 * which emails the team. Answers like the new endpoint as far as this form
 * cares: ok, or a status.
 */
async function legacyEarlyAccess(body: {
  email: string;
  type: string;
  organization_name: string;
  updates: boolean;
}): Promise<{ ok: boolean; status: number }> {
  try {
    // The guarded route (`form-guard.ts`) wants a token proving the
    // submission came from a page this server rendered.
    const res = await fetch("/api/early-access", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: body.email,
        type: body.type,
        company: body.organization_name,
        updates: body.updates,
        formToken: await formToken(),
      }),
    });
    if (res.status === 403) clearFormToken();
    return { ok: res.ok, status: res.status };
  } catch {
    return { ok: false, status: 0 };
  }
}
import { cn } from "@/lib/utils";

const CONTACT_EMAIL = "info@paranine.com";
const NAME_MAX = 255;
/** A form filled and sent faster than this was not typed by a person. */
const MIN_FILL_MS = 1200;

type EarlyAccessContextValue = {
  /** Mode-aware: the form, or sign-up when the backend's mode is "open". */
  open: () => void;
  /** Always the form, for a page that has just learned sign-up is closed. */
  openForm: () => void;
  close: () => void;
};
const EarlyAccessContext = createContext<EarlyAccessContextValue | null>(null);

export function useEarlyAccess() {
  const ctx = useContext(EarlyAccessContext);
  if (!ctx) throw new Error("useEarlyAccess must be used within <EarlyAccessProvider>");
  return ctx;
}

export function EarlyAccessProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  // Ask once per page load, ahead of the first click.
  useEffect(() => {
    void loadSignupMode();
  }, []);

  const open = useCallback(() => {
    void loadSignupMode().then((mode) => {
      if (mode === "open") {
        window.location.assign("/dashboard/signup");
        return;
      }
      setIsOpen(true);
    });
  }, []);
  const openForm = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const value = useMemo(() => ({ open, openForm, close }), [open, openForm, close]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, close]);

  return (
    <EarlyAccessContext.Provider value={value}>
      {children}
      <EarlyAccessModal isOpen={isOpen} onClose={close} />
    </EarlyAccessContext.Provider>
  );
}

type Status = "idle" | "loading" | "success";
type FieldKey = "email" | "organization" | "project";

const FIELD_CLASS =
  "mt-2 w-full rounded-lg border bg-background px-3.5 py-2.5 font-sans text-sm text-foreground outline-none transition-colors placeholder:text-muted-2 focus:border-border-strong";

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="mt-4 block font-sans text-xs font-medium text-foreground">
      {children}
    </label>
  );
}

function FieldMessage({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p id={id} role="alert" className="mt-1.5 font-sans text-xs text-danger">
      {children}
    </p>
  );
}

function EarlyAccessModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [type, setType] = useState<"solo" | "enterprise">("solo");
  const [email, setEmail] = useState("");
  const [organization, setOrganization] = useState("");
  const [project, setProject] = useState("");
  const [website, setWebsite] = useState("");
  // Product-update emails, ticked by default (decision of 2026-09-22). Only
  // the UI's own route records it, so the box shows only where that route is
  // the one used: a backend without Ticket 030.
  const [status, setStatus] = useState<Status>("idle");
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [formError, setFormError] = useState("");
  const openedAt = useRef(0);

  // Fresh form every time it opens.
  useEffect(() => {
    if (isOpen) {
      setType("solo");
      setEmail("");
      setOrganization("");
      setProject("");
      setWebsite("");
      setStatus("idle");
      setFieldErrors({});
      setFormError("");
      openedAt.current = Date.now();
    }
  }, [isOpen]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === "loading") return;
    const address = email.trim();
    const org = organization.trim();
    const proj = project.trim();

    const errors: Partial<Record<FieldKey, string>> = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address) || address.length > 254) {
      errors.email = "Enter a valid email address.";
    }
    if (!org) errors.organization = "Enter your organization's name.";
    else if (org.length > NAME_MAX) errors.organization = "Organization name must be 1–255 characters.";
    if (proj.length > NAME_MAX) errors.project = "Project name must be 1–255 characters.";
    setFieldErrors(errors);
    setFormError("");
    if (Object.keys(errors).length > 0) return;

    // A script fills every field, the hidden one included, and sends at once.
    // It is shown the same success as a person and nothing is sent.
    if (website || Date.now() - openedAt.current < MIN_FILL_MS) {
      setStatus("success");
      return;
    }

    setStatus("loading");
    const res = await requestEarlyAccess({
      email: address,
      organization_name: org,
      project_name: proj || undefined,
      type,
      website,
    });
    // Only if the endpoint itself is missing: the guarded route GCP still has.
    if (!res.ok && res.status === 404 && res.code !== "not_proxied") {
      const legacy = await legacyEarlyAccess({ email: address, type, organization_name: org, updates: false });
      if (legacy.ok) {
        setStatus("success");
        return;
      }
      setStatus("idle");
      setFormError(
        legacy.status === 429
          ? "Too many requests. Try again later."
          : `Something went wrong. Please email us at ${CONTACT_EMAIL}.`,
      );
      return;
    }
    if (res.ok) {
      setStatus("success");
      return;
    }
    setStatus("idle");
    switch (res.code) {
      case "invalid_email":
        setFieldErrors({ email: "Enter a valid email address." });
        return;
      case "organization_name_required":
        setFieldErrors({ organization: "Enter your organization's name." });
        return;
      case "invalid_project_name":
        setFieldErrors({ project: "Project name must be 1–255 characters." });
        return;
      case "organization_name_taken":
        setFieldErrors({ organization: "That name is taken. Try another." });
        return;
    }
    if (res.status === 429) {
      setFormError("Too many requests. Try again later.");
      return;
    }
    setFormError(`Something went wrong. Please email us at ${CONTACT_EMAIL}.`);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto p-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="early-access-title"
        >
          <div
            className="fixed inset-0 bg-background/70 backdrop-blur-md"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            className="relative my-auto w-full max-w-md overflow-hidden rounded-lg border border-border-strong bg-card p-8 text-center shadow-2xl"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full text-muted transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M5 5l14 14M19 5L5 19" />
              </svg>
            </button>

            <div className="relative mx-auto mb-5 grid h-14 w-14 place-items-center rounded-lg border border-border bg-background">
              <LogoMark className="h-7 w-7 text-foreground" />
            </div>

            {status === "success" ? (
              <>
                <span className="eyebrow">Request received</span>
                <h2 id="early-access-title" className="mt-3 font-serif text-[1.7rem] leading-tight tracking-tight">
                  Check your inbox
                </h2>
                <p className="mt-3 text-[0.97rem] leading-relaxed text-muted">
                  We&apos;ve sent a confirmation to{" "}
                  <span className="text-foreground">{email.trim()}</span>. We&apos;ll email you again
                  when your account is ready.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-6 inline-flex w-full items-center justify-center rounded-lg bg-foreground px-6 py-3.5 font-sans text-sm font-medium text-background transition-transform hover:scale-[1.015] active:scale-[0.99]"
                >
                  Done
                </button>
              </>
            ) : (
              <>
                <span className="eyebrow">Early access</span>
                <h2 id="early-access-title" className="mt-3 font-serif text-[1.7rem] leading-tight tracking-tight">
                  Get early access
                </h2>
                <p className="mt-3 text-[0.95rem] leading-relaxed text-muted">
                  Tell us who you are. Once your request is approved, we&apos;ll email you a link to
                  activate your account, with Rs 1,000 of credit to start.
                </p>

                <form onSubmit={submit} noValidate className="mt-6 text-left">
                  <p className="font-sans text-xs font-medium text-foreground">
                    Are you a solo developer or an enterprise?
                  </p>
                  <div className="mt-2 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Who you are">
                    {(
                      [
                        { key: "solo", label: "Solo developer" },
                        { key: "enterprise", label: "Enterprise" },
                      ] as const
                    ).map((o) => (
                      <button
                        key={o.key}
                        type="button"
                        role="radio"
                        aria-checked={type === o.key}
                        onClick={() => setType(o.key)}
                        className={cn(
                          "rounded-lg border px-3 py-2.5 font-sans text-sm transition-colors",
                          type === o.key
                            ? "border-foreground bg-foreground/[0.05] text-foreground"
                            : "border-border text-muted hover:border-border-strong hover:text-foreground",
                        )}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>

                  <FieldLabel htmlFor="ea-email">Work email</FieldLabel>
                  <input
                    id="ea-email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    maxLength={254}
                    aria-invalid={!!fieldErrors.email || undefined}
                    aria-describedby={fieldErrors.email ? "ea-email-error" : undefined}
                    className={cn(FIELD_CLASS, fieldErrors.email ? "border-danger/60" : "border-border")}
                  />
                  {fieldErrors.email && <FieldMessage id="ea-email-error">{fieldErrors.email}</FieldMessage>}

                  <FieldLabel htmlFor="ea-org">Organization name</FieldLabel>
                  <input
                    id="ea-org"
                    type="text"
                    required
                    autoComplete="organization"
                    value={organization}
                    onChange={(e) => setOrganization(e.target.value)}
                    placeholder="Acme"
                    maxLength={NAME_MAX}
                    aria-invalid={!!fieldErrors.organization || undefined}
                    aria-describedby={fieldErrors.organization ? "ea-org-error" : "ea-org-hint"}
                    className={cn(FIELD_CLASS, fieldErrors.organization ? "border-danger/60" : "border-border")}
                  />
                  {fieldErrors.organization ? (
                    <FieldMessage id="ea-org-error">{fieldErrors.organization}</FieldMessage>
                  ) : (
                    <p id="ea-org-hint" className="mt-1.5 font-sans text-xs text-muted-2">
                      This becomes your organization&apos;s name in Paranine.
                    </p>
                  )}

                  <FieldLabel htmlFor="ea-project">
                    Project name <span className="font-normal text-muted-2">(optional)</span>
                  </FieldLabel>
                  <input
                    id="ea-project"
                    type="text"
                    autoComplete="off"
                    value={project}
                    onChange={(e) => setProject(e.target.value)}
                    placeholder="Default"
                    maxLength={NAME_MAX}
                    aria-invalid={!!fieldErrors.project || undefined}
                    aria-describedby={fieldErrors.project ? "ea-project-error" : undefined}
                    className={cn(FIELD_CLASS, fieldErrors.project ? "border-danger/60" : "border-border")}
                  />
                  {fieldErrors.project && <FieldMessage id="ea-project-error">{fieldErrors.project}</FieldMessage>}

                  {/* Honeypot. Visually hidden by position, not display:none,
                      which some bots skip; out of the tab order and away from
                      autofill, so a person never fills it. */}
                  <div aria-hidden="true" className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden">
                    <label htmlFor="ea-website">Website</label>
                    <input
                      id="ea-website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </div>

                  <FormConsent className="mt-3" />

                  {formError && (
                    <p role="alert" className="mt-3 font-sans text-xs text-danger">
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="group mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-foreground px-6 py-3.5 font-sans text-sm font-medium text-background transition-transform hover:scale-[1.015] active:scale-[0.99] disabled:opacity-60"
                  >
                    {status === "loading" ? "Sending…" : "Request early access"}
                    {status !== "loading" && (
                      <svg className="transition-transform group-hover:translate-x-0.5" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    )}
                  </button>
                </form>

                <p className="mt-3 font-sans text-xs text-muted-2">
                  or email us at{" "}
                  <a className="text-foreground underline-offset-2 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
                    {CONTACT_EMAIL}
                  </a>
                </p>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
